import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { getDocs, doc, getDoc, setDoc, addDoc, onSnapshot, collection, serverTimestamp, query, where, limit } from '../firebase.config';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currencyUtils';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import useActiveCompanyProfile from '../hooks/useActiveCompanyProfile';
import { buildBillPreviewCompanyDetails } from '../utils/dataAdapter';
import toast from 'react-hot-toast';
import { ChevronDown } from 'lucide-react';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import GlobalExportButtons from './GlobalExportButtons';
import { buildReportFilename } from './Reports/exportUtils';
import {
  extractServiceJobsFromBills,
  filterServiceJobs,
  indexPartiesById,
  getServiceTopSummary,
  getTagWiseAggregation,
  getVendorWiseAggregation,
  enrichTagWiseRowsWithMeta,
  groupTagWiseRowsByRateBasis,
  buildTagFilterGroups,
} from '../utils/services';
import {
  getVendorsForServiceTag,
  findServiceTagMeta,
  calculateInternalCostFromTag,
  getServiceTagDocId,
  normalizeTagName,
  buildPreferredVendorPatch,
  promotePreferredVendorId,
  normalizePreferredVendorIds,
  isPreferredVendorForTag,
  enrichJobsWithSuggestedVendors,
  suggestVendorForTag,
  sortVendorsForTag,
} from '../utils/serviceTagRegistry';

const SERVICE_STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-900 border-amber-200',
  done: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  paid: 'bg-violet-100 text-violet-900 border-violet-200',
};

const ServiceOperations = ({ db, userId, appId, companyId }) => {
  const location = useLocation();
  const { currentCompany, companyProfile } = useActiveCompanyProfile();
  const resolvedCompanyId = companyId || currentCompany?.companyId || currentCompany?.id || null;
  const [jobs, setJobs] = useState([]);
  const [parties, setParties] = useState([]);
  const [serviceTags, setServiceTags] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [viewMode, setViewMode] = useState('operations');
  const [sortBy, setSortBy] = useState('billDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [reportTab, setReportTab] = useState('vendors');
  const [showVendorPaymentModal, setShowVendorPaymentModal] = useState(false);
  const [savingVendorPayment, setSavingVendorPayment] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedBillKeys, setExpandedBillKeys] = useState(() => new Set());
  const [expandedVendorKeys, setExpandedVendorKeys] = useState(() => new Set());
  const [auxLoaded, setAuxLoaded] = useState(false);
  const saveQueueRef = useRef(new Map());
  const saveTimerRef = useRef(null);
  const [vendorPaymentForm, setVendorPaymentForm] = useState({
    vendorName: '',
    vendorId: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMode: 'Cash',
    reference: '',
    notes: '',
    attachEmployeeWallet: false,
    employeeId: '',
  });
  const [filters, setFilters] = useState({
    vendor: '',
    tag: '',
    status: '',
    invoiceVisibleOnly: false,
    hasVendor: 'all',
    rateBasis: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tag = params.get('tag') || '';
    const status = params.get('status') || '';
    const vendor = params.get('vendor') || '';
    if (tag || status || vendor) {
      setFilters(prev => ({
        ...prev,
        tag: tag || prev.tag,
        status: status || prev.status,
        vendor: vendor || prev.vendor,
      }));
    }
  }, [location.search]);

  const applyVendorPaymentsFromSnapshots = useCallback((servicePaymentRows, paymentRows) => {
    if (servicePaymentRows.length > 0) {
      setVendorPayments(servicePaymentRows.map(p => ({
        partyId: p.vendorId || '',
        partyName: p.vendorName || '',
        vendorId: p.vendorId || '',
        vendorName: p.vendorName || '',
        amount: p.amount || 0,
        source: 'service-operations',
      })));
      return;
    }
    setVendorPayments(paymentRows.filter(p => {
      const category = String(p.category || '').toLowerCase();
      const source = String(p.source || '').toLowerCase();
      return category === 'service vendor payment' || source === 'service-operations';
    }).map(p => ({
      partyId: p.partyId || '',
      partyName: p.partyName || '',
      vendorId: p.partyId || '',
      vendorName: p.partyName || '',
      amount: p.amount || p.totalAmount || 0,
      source: 'service-operations',
    })));
  }, []);

  const loadAuxiliaryData = useCallback(async () => {
    if (!db || !userId || !resolvedCompanyId || auxLoaded) return;
    try {
      const employeesRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'employees') || collection(db, 'companies', resolvedCompanyId, 'employees');
      const paymentsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'payments') || collection(db, 'companies', resolvedCompanyId, 'payments');
      const serviceVendorPaymentsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'serviceVendorPayments') || collection(db, 'companies', resolvedCompanyId, 'serviceVendorPayments');
      const [employeesSnap, paymentsSnap, serviceVendorPaymentsSnap] = await Promise.all([
        employeesRef ? getDocs(employeesRef) : Promise.resolve({ docs: [] }),
        paymentsRef ? getDocs(paymentsRef) : Promise.resolve({ docs: [] }),
        serviceVendorPaymentsRef ? getDocs(serviceVendorPaymentsRef) : Promise.resolve({ docs: [] }),
      ]);
      setEmployees(employeesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      applyVendorPaymentsFromSnapshots(
        serviceVendorPaymentsSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        paymentsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setAuxLoaded(true);
    } catch (error) {
      console.error('ServiceOperations aux load error:', error);
    }
  }, [db, userId, appId, resolvedCompanyId, auxLoaded, applyVendorPaymentsFromSnapshots]);

  useEffect(() => {
    const run = async () => {
      if (!db || !userId || !resolvedCompanyId) {
        setJobs([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setAuxLoaded(false);
      try {
        const salesRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'salesBills') || collection(db, 'companies', resolvedCompanyId, 'salesBills');
        const challansRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'challans') || collection(db, 'companies', resolvedCompanyId, 'challans');
        const quotationsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'quotations') || collection(db, 'companies', resolvedCompanyId, 'quotations');
        const partiesRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'parties') || collection(db, 'companies', resolvedCompanyId, 'parties');
        const itemsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'items') || collection(db, 'companies', resolvedCompanyId, 'items');
        const serviceTagsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'serviceTags') || collection(db, 'companies', resolvedCompanyId, 'serviceTags');
        const [salesSnap, challansSnap, quotationsSnap, partiesSnap, itemsSnap, serviceTagsSnap] = await Promise.all([
          salesRef ? getDocs(salesRef) : Promise.resolve({ docs: [] }),
          challansRef ? getDocs(challansRef) : Promise.resolve({ docs: [] }),
          quotationsRef ? getDocs(quotationsRef) : Promise.resolve({ docs: [] }),
          partiesRef ? getDocs(partiesRef) : Promise.resolve({ docs: [] }),
          itemsRef ? getDocs(itemsRef) : Promise.resolve({ docs: [] }),
          serviceTagsRef ? getDocs(serviceTagsRef) : Promise.resolve({ docs: [] }),
        ]);
        const bills = [
          ...salesSnap.docs.map(d => ({ id: d.id, ...d.data(), docType: d.data()?.docType || 'invoice' })),
          ...challansSnap.docs.map(d => ({ id: d.id, ...d.data(), docType: d.data()?.docType || 'challan' })),
          ...quotationsSnap.docs.map(d => ({ id: d.id, ...d.data(), docType: d.data()?.docType || 'quotation' })),
        ];
        const partyRows = partiesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const items = itemsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const itemMap = items.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});
        const tagsList = serviceTagsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setParties(partyRows);
        setServiceTags(tagsList);
        const partiesById = indexPartiesById(partyRows);
        const extractedJobs = extractServiceJobsFromBills(bills, { partiesById, itemsById: itemMap, includeTagBackfill: true });
        setJobs(enrichJobsWithSuggestedVendors(extractedJobs, partyRows, tagsList));
      } catch (error) {
        console.error('ServiceOperations load error:', error);
        setJobs([]);
        toast.error('Failed to load service operations.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [db, userId, appId, resolvedCompanyId]);

  useEffect(() => {
    if (!db || !userId || !resolvedCompanyId) return undefined;
    const tagsRef =
      getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'serviceTags') ||
      collection(db, 'companies', resolvedCompanyId, 'serviceTags');
    const partiesRef =
      getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'parties') ||
      collection(db, 'companies', resolvedCompanyId, 'parties');
    const unsubs = [];
    if (tagsRef) {
      unsubs.push(
        onSnapshot(tagsRef, (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setServiceTags(list);
        })
      );
    }
    if (partiesRef) {
      unsubs.push(
        onSnapshot(partiesRef, (snap) => {
          setParties(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        })
      );
    }
    return () => unsubs.forEach((u) => u());
  }, [db, userId, appId, resolvedCompanyId]);

  useEffect(() => {
    if (viewMode === 'reports' || showVendorPaymentModal) {
      void loadAuxiliaryData();
    }
  }, [viewMode, showVendorPaymentModal, loadAuxiliaryData]);

  const resolveCollectionFromDocType = (docType) => {
    if (docType === 'challan') return 'challans';
    if (docType === 'quotation') return 'quotations';
    return 'salesBills';
  };

  const getJobKey = (job) => `${job.billId}_${job.rowIndex}_${job.serviceName}`;

  const saveJobUpdate = async (job, options = {}) => {
    const { silent = false } = options;
    if (!db || !userId || !resolvedCompanyId || !job?.billId) return;
    const key = `${job.billId}_${job.rowIndex}_${job.serviceName}`;
    setSavingKey(key);
    try {
      const collectionName = resolveCollectionFromDocType(job.docType);
      const colRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, collectionName) || collection(db, 'companies', resolvedCompanyId, collectionName);
      if (!colRef) return;
      const billRef = doc(colRef, job.billId);
      const snap = await getDoc(billRef);
      if (!snap.exists()) return;
      const data = snap.data();
      const rows = Array.isArray(data.rows) ? [...data.rows] : [];
      if (!rows[job.rowIndex]) return;
      const row = { ...rows[job.rowIndex] };
      const tags = Array.isArray(row.tags) ? row.tags : [];
      if (job.serviceName && !tags.some(t => String(t || '').toLowerCase() === String(job.serviceName).toLowerCase())) {
        tags.push(job.serviceName);
      }
      const existingServices = Array.isArray(row.services) ? [...row.services] : [];
      const idx = existingServices.findIndex(s => String(s?.serviceName || '').toLowerCase() === String(job.serviceName || '').toLowerCase());
      const selectedVendor = parties.find(p => p.id === job.vendorId);
      const nextService = {
        ...(idx >= 0 ? existingServices[idx] : {}),
        serviceName: job.serviceName,
        vendorId: job.vendorId || '',
        vendorName: selectedVendor?.firmName || job.vendorName || '',
        internalCost: parseFloat(job.internalCost) || 0,
        customerCharge: parseFloat(job.customerCharge) || 0,
        showOnInvoice: Boolean(job.showOnInvoice),
        status: job.status || 'pending',
        createdAt: (idx >= 0 ? existingServices[idx]?.createdAt : null) || new Date().toISOString(),
      };
      if (idx >= 0) existingServices[idx] = nextService;
      else existingServices.push(nextService);
      rows[job.rowIndex] = { ...row, tags, services: existingServices };
      await setDoc(billRef, { rows }, { merge: true });
      setJobs(prev => prev.map(j => {
        const jKey = `${j.billId}_${j.rowIndex}_${j.serviceName}`;
        if (jKey !== key) return j;
        return {
          ...j,
          vendorId: nextService.vendorId || '',
          vendorName: nextService.vendorName || '',
          status: nextService.status || 'pending',
          internalCost: nextService.internalCost || 0,
          customerCharge: nextService.customerCharge || 0,
          showOnInvoice: Boolean(nextService.showOnInvoice),
        };
      }));
      if (!silent) toast.success('Saved');
    } catch (error) {
      console.error('Failed to save service job update:', error);
      toast.error('Could not save. Check connection and try again.');
    } finally {
      setSavingKey('');
    }
  };

  const scheduleSave = useCallback((job) => {
    if (!job) return;
    saveQueueRef.current.set(getJobKey(job), job);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      const batch = [...saveQueueRef.current.values()];
      saveQueueRef.current.clear();
      batch.forEach((j) => saveJobUpdate(j, { silent: true }));
    }, 700);
  }, []);

  const handleInlineUpdate = (jobKey, field, value) => {
    setJobs((prev) => {
      const next = prev.map((job) => (
        getJobKey(job) === jobKey ? { ...job, [field]: value } : job
      ));
      const updated = next.find((job) => getJobKey(job) === jobKey);
      if (updated) scheduleSave(updated);
      return next;
    });
  };

  const patchJob = (jobKey, patch) => {
    setJobs((prev) => {
      const next = prev.map((job) => (
        getJobKey(job) === jobKey ? { ...job, ...patch } : job
      ));
      const updated = next.find((job) => getJobKey(job) === jobKey);
      if (updated) scheduleSave(updated);
      return next;
    });
  };

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const filteredJobs = useMemo(() => {
    const hasVendorFilter = filters.hasVendor === 'yes' ? true : (filters.hasVendor === 'no' ? false : undefined);
    return filterServiceJobs(jobs, {
      vendor: filters.vendor,
      tag: filters.tag,
      status: filters.status,
      invoiceVisibleOnly: filters.invoiceVisibleOnly,
      hasVendor: hasVendorFilter,
    });
  }, [jobs, filters]);

  const sortedFilteredJobs = useMemo(() => {
    const getValue = (job) => {
      if (sortBy === 'billDate') return new Date(job.billDate || 0).getTime();
      if (sortBy === 'vendorName') return (job.vendorName || '').toLowerCase();
      if (sortBy === 'status') return (job.status || '').toLowerCase();
      if (sortBy === 'internalCost') return Number(job.internalCost || 0);
      if (sortBy === 'customerCharge') return Number(job.customerCharge || 0);
      return (job.billNo || '').toLowerCase();
    };
    const sorted = [...filteredJobs].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv));
    });
    return sortDirection === 'asc' ? sorted : sorted.reverse();
  }, [filteredJobs, sortBy, sortDirection]);

  const vendorOptions = useMemo(() => {
    const map = new Map();
    jobs.forEach(job => {
      const key = (job.vendorId || job.vendorName || '').toLowerCase();
      if (!key) return;
      map.set(key, job.vendorName || job.vendorId);
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [jobs]);

  const vendorPaymentsMap = useMemo(() => {
    const map = new Map();
    vendorPayments.forEach((payment) => {
      const amount = parseFloat(payment.amount) || 0;
      const keyById = String(payment.vendorId || payment.partyId || '').toLowerCase();
      const keyByName = String(payment.vendorName || payment.partyName || '').toLowerCase();
      if (keyById) map.set(keyById, (map.get(keyById) || 0) + amount);
      if (keyByName) map.set(keyByName, (map.get(keyByName) || 0) + amount);
    });
    return map;
  }, [vendorPayments]);

  const partyVendorOptions = useMemo(() => {
    return (parties || []).map(p => ({ value: p.id, label: p.firmName || p.name || p.partyName || p.id }));
  }, [parties]);

  const rememberPreferredVendorForTag = useCallback(
    async (serviceName, vendorId) => {
      if (!vendorId || !db || !resolvedCompanyId) return;
      const vendor = parties.find((p) => p.id === vendorId);
      if (!vendor) return;
      const normalized = normalizeTagName(serviceName);
      if (!normalized) return;
      const col =
        getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'serviceTags') ||
        collection(db, 'companies', resolvedCompanyId, 'serviceTags');
      if (!col) return;
      try {
        const tagMeta = findServiceTagMeta(serviceTags, normalized);
        const ids = promotePreferredVendorId(normalizePreferredVendorIds(tagMeta), vendor.id);
        await setDoc(
          doc(col, getServiceTagDocId(serviceTags, normalized)),
          {
            normalizedName: normalized,
            name: normalized,
            ...buildPreferredVendorPatch(ids, parties),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.error('rememberPreferredVendorForTag', e);
      }
    },
    [db, resolvedCompanyId, appId, userId, parties, serviceTags]
  );

  const getVendorOptionsForJob = useCallback(
    (job) => {
      const tagMeta = findServiceTagMeta(serviceTags, job?.serviceName);
      let list = getVendorsForServiceTag(parties, job?.serviceName, tagMeta);
      if (list.length === 0) {
        list = sortVendorsForTag(
          parties.filter((p) => p.partyType === 'Seller' || p.partyType === 'Both'),
          tagMeta
        );
      }
      const prefIds = normalizePreferredVendorIds(tagMeta);
      return list.map((p) => ({
        value: p.id,
        label: p.firmName || p.name || p.partyName || p.id,
        isFavorite: isPreferredVendorForTag(tagMeta, p.id),
        favRank: prefIds.indexOf(p.id),
      }));
    },
    [parties, serviceTags]
  );

  const getSuggestedVendorForJob = useCallback(
    (job) => {
      const tagMeta = findServiceTagMeta(serviceTags, job?.serviceName);
      return suggestVendorForTag(parties, job?.serviceName, tagMeta);
    },
    [parties, serviceTags]
  );

  const tagOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach(job => {
      if (job.serviceName) set.add(job.serviceName);
    });
    return Array.from(set);
  }, [jobs]);

  const tagFilterGroups = useMemo(
    () => buildTagFilterGroups(tagOptions, serviceTags),
    [tagOptions, serviceTags]
  );

  const topSummary = useMemo(() => getServiceTopSummary(filteredJobs), [filteredJobs]);
  const tagWiseRows = useMemo(() => getTagWiseAggregation(filteredJobs), [filteredJobs]);
  const enrichedTagWiseRows = useMemo(
    () => enrichTagWiseRowsWithMeta(tagWiseRows, serviceTags),
    [tagWiseRows, serviceTags]
  );
  const tagWiseRowsForReports = useMemo(() => {
    if (!filters.rateBasis) return enrichedTagWiseRows;
    return enrichedTagWiseRows.filter((row) => row.rateBasis === filters.rateBasis);
  }, [enrichedTagWiseRows, filters.rateBasis]);
  const tagWiseGroupedByBasis = useMemo(
    () => groupTagWiseRowsByRateBasis(tagWiseRowsForReports),
    [tagWiseRowsForReports]
  );
  const rateBasisFilterOptions = useMemo(() => {
    const grouped = groupTagWiseRowsByRateBasis(enrichedTagWiseRows);
    return grouped.map((g) => ({
      value: g.rateBasis,
      label: g.rateBasisShortLabel,
      count: g.usageCount,
    }));
  }, [enrichedTagWiseRows]);
  const vendorWiseRows = useMemo(() => {
    const rows = getVendorWiseAggregation(filteredJobs);
    return rows.map((row) => {
      const key = String(row.vendorKey || '').toLowerCase();
      const byKey = vendorPaymentsMap.get(key) || 0;
      const byName = vendorPaymentsMap.get(String(row.vendorName || '').toLowerCase()) || 0;
      const paidViaServicePayments = Math.max(byKey, byName);
      const pendingAfterPayments = Math.max(0, (row.pendingPayable || 0) - paidViaServicePayments);
      return {
        ...row,
        paidAmount: (row.paidAmount || 0) + paidViaServicePayments,
        pendingPayable: pendingAfterPayments,
      };
    });
  }, [filteredJobs, vendorPaymentsMap]);

  const toggleVendorJobsExpanded = useCallback((vendorKey) => {
    setExpandedVendorKeys((prev) => {
      const next = new Set(prev);
      if (next.has(vendorKey)) next.delete(vendorKey);
      else next.add(vendorKey);
      return next;
    });
  }, []);

  const expandAllVendorJobs = useCallback(() => {
    setExpandedVendorKeys(new Set(vendorWiseRows.map((r) => r.vendorKey)));
  }, [vendorWiseRows]);

  const collapseAllVendorJobs = useCallback(() => {
    setExpandedVendorKeys(new Set());
  }, []);

  const companyDetailsForExport = useMemo(
    () => buildBillPreviewCompanyDetails(companyProfile || {}, {}),
    [companyProfile]
  );

  const topSummaryExport = useMemo(() => ([{
    metric: 'Total Service Jobs', value: topSummary.totalServiceJobs || 0,
  }, {
    metric: 'Pending Jobs', value: topSummary.pendingJobs || 0,
  }, {
    metric: 'Done Jobs', value: topSummary.doneJobs || 0,
  }, {
    metric: 'Paid Jobs', value: topSummary.paidJobs || 0,
  }, {
    metric: 'Total Internal Cost', value: topSummary.totalInternalCost || 0,
  }, {
    metric: 'Total Customer Service Charge', value: topSummary.totalCustomerServiceCharge || 0,
  }, {
    metric: 'Total Vendor Payable', value: topSummary.totalVendorPayable || 0,
  }, {
    metric: 'Invoice-visible Service Revenue', value: topSummary.invoiceVisibleServiceRevenue || 0,
  }]), [topSummary]);

  const exportAllServiceReports = () => {
    try {
      const toCsvSection = (title, rows) => {
        if (!rows || rows.length === 0) return `${title}\nNo data\n`;
        const headers = Object.keys(rows[0]);
        const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = [
          title,
          headers.join(','),
          ...rows.map(row => headers.map(h => escape(Array.isArray(row[h]) ? row[h].join(' | ') : row[h])).join(',')),
          ''
        ];
        return `${lines.join('\n')}\n`;
      };

      let csv = '';
      csv += toCsvSection('Service Operations - Top Summary', topSummaryExport);
      csv += toCsvSection('Service Operations - Tag Wise', tagWiseRows);
      csv += toCsvSection('Service Operations - Vendor Wise', vendorWiseRows);

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const filename = buildReportFilename({
        prefix: 'service-operations-all-reports',
        companyDetails: companyDetailsForExport,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Combined service reports exported.');
    } catch (error) {
      console.error('Failed to export all service reports:', error);
      toast.error('Failed to export combined service reports.');
    }
  };

  const reportTabExportPayload = useMemo(() => {
    if (reportTab === 'tags') {
      return {
        data: tagWiseRowsForReports,
        columns: [
          { key: 'tag', label: 'Tag' },
          { key: 'rateBasisLabel', label: 'Classification' },
          { key: 'defaultRate', label: 'Default Rate' },
          { key: 'usageCount', label: 'Jobs' },
          { key: 'pendingCount', label: 'Pending' },
          { key: 'doneCount', label: 'Done' },
          { key: 'paidCount', label: 'Paid' },
          { key: 'avgInternalPerJob', label: 'Avg Internal / Job' },
          { key: 'totalInternalCost', label: 'Internal Cost' },
          { key: 'totalCustomerCharge', label: 'Customer Charge' },
        ],
        filename: 'service-operations-by-tag',
        title: 'Service Operations — By Tag',
      };
    }
    return {
      data: vendorWiseRows,
      columns: [
        { key: 'vendorName', label: 'Vendor' },
        { key: 'totalJobs', label: 'Jobs' },
        { key: 'pendingJobs', label: 'Pending Jobs' },
        { key: 'totalInternalCost', label: 'Internal Cost' },
        { key: 'pendingPayable', label: 'Pending Payable' },
        { key: 'paidAmount', label: 'Paid' },
      ],
      filename: 'service-operations-by-vendor',
      title: 'Service Operations — By Vendor',
    };
  }, [reportTab, tagWiseRowsForReports, vendorWiseRows]);

  const generateServicePaymentReceiptNumber = async () => {
    const currentYear = new Date().getFullYear();
    const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
    const paymentType = 'P';
    try {
      const paymentsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'payments') || collection(db, 'companies', resolvedCompanyId, 'payments');
      const paymentsSnapshot = await getDocs(paymentsRef);
      let highestSequence = 0;
      paymentsSnapshot.forEach((paymentDoc) => {
        const payment = paymentDoc.data() || {};
        const receipt = String(payment.receiptNumber || '');
        const regexPattern = new RegExp(`^PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)$`);
        const match = receipt.match(regexPattern);
        if (match) {
          const sequence = parseInt(match[1], 10);
          if (sequence > highestSequence) highestSequence = sequence;
        }
      });
      const nextSequence = highestSequence + 1;
      return `PR${paymentType}${financialYear}/${nextSequence.toString().padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating service payment receipt number:', error);
      return `PR${paymentType}${financialYear}/001`;
    }
  };

  const ensureUniqueReceiptNumber = async (baseReceiptNumber) => {
    const paymentsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'payments') || collection(db, 'companies', resolvedCompanyId, 'payments');
    let candidate = baseReceiptNumber;
    let tries = 0;
    while (tries < 5) {
      const existingQuery = query(paymentsRef, where('receiptNumber', '==', candidate), limit(1));
      const snap = await getDocs(existingQuery);
      if (snap.empty) return candidate;
      const seqMatch = candidate.match(/\/(\d+)$/);
      const nextSeq = seqMatch ? String(parseInt(seqMatch[1], 10) + 1).padStart(seqMatch[1].length, '0') : String(Date.now()).slice(-3);
      candidate = candidate.replace(/\/\d+$/, `/${nextSeq}`);
      tries += 1;
    }
    return `${baseReceiptNumber}-R${Date.now().toString().slice(-3)}`;
  };

  const openVendorPayment = (vendorRow) => {
    const matchedParty = parties.find((p) => {
      const name = String(p.firmName || p.name || p.partyName || '').toLowerCase();
      const vendorName = String(vendorRow.vendorName || '').toLowerCase();
      return name && vendorName && name === vendorName;
    });
    const suggestedAmount = Math.max(
      parseFloat(vendorRow.pendingPayable) || 0,
      (parseFloat(vendorRow.totalInternalCost) || 0) - (parseFloat(vendorRow.paidAmount) || 0),
      0
    );
    setVendorPaymentForm({
      vendorName: vendorRow.vendorName || '',
      vendorId: matchedParty?.id || '',
      amount: suggestedAmount,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMode: 'Cash',
      reference: '',
      notes: `Service operation payable settlement for vendor ${vendorRow.vendorName || 'Unassigned Vendor'}`,
      attachEmployeeWallet: false,
      employeeId: '',
    });
    setShowVendorPaymentModal(true);
  };

  const saveVendorPayment = async () => {
    if (!db || !userId || !resolvedCompanyId) {
      toast.error('Context missing. Please refresh and try again.');
      return;
    }
    const amount = parseFloat(vendorPaymentForm.amount) || 0;
    if (amount <= 0) {
      toast.error('Enter valid payment amount.');
      return;
    }
    setSavingVendorPayment(true);
    try {
      const paymentsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'payments') || collection(db, 'companies', resolvedCompanyId, 'payments');
      const serviceVendorPaymentsRef = getCollectionForCompany(db, { companyId: resolvedCompanyId, appId, userId }, 'serviceVendorPayments') || collection(db, 'companies', resolvedCompanyId, 'serviceVendorPayments');
      const generated = await generateServicePaymentReceiptNumber();
      const receiptNo = await ensureUniqueReceiptNumber(generated);
      const notes = `${vendorPaymentForm.notes || ''}`.trim();
      const resolvedVendorId = vendorPaymentForm.vendorId || (parties.find((p) => {
        const name = String(p.firmName || p.name || p.partyName || '').toLowerCase();
        const target = String(vendorPaymentForm.vendorName || '').toLowerCase();
        return name && target && name === target;
      })?.id || '');
      const paymentDocRef = await addDoc(paymentsRef, {
        receiptNumber: receiptNo,
        paymentDate: vendorPaymentForm.paymentDate,
        totalAmount: amount,
        amount,
        partyId: resolvedVendorId,
        partyName: vendorPaymentForm.vendorName || 'Unassigned Vendor',
        partyFirmName: vendorPaymentForm.vendorName || 'Unassigned Vendor',
        paymentMode: vendorPaymentForm.paymentMode,
        reference: vendorPaymentForm.reference || '',
        notes: notes || `Service vendor payment: ${vendorPaymentForm.vendorName || 'Unassigned Vendor'}`,
        paymentType: 'khata',
        type: 'payments',
        category: 'Service Vendor Payment',
        transactionType: 'outflow',
        description: `Service operations payable settlement - ${vendorPaymentForm.vendorName || 'Unassigned Vendor'} (${receiptNo})`,
        source: 'service-operations',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await addDoc(serviceVendorPaymentsRef, {
        receiptNumber: receiptNo,
        paymentDate: vendorPaymentForm.paymentDate,
        amount,
        vendorId: resolvedVendorId,
        vendorName: vendorPaymentForm.vendorName || 'Unassigned Vendor',
        paymentMode: vendorPaymentForm.paymentMode,
        reference: vendorPaymentForm.reference || '',
        notes: notes || '',
        description: `Service operations payable settlement - ${vendorPaymentForm.vendorName || 'Unassigned Vendor'} (${receiptNo})`,
        source: 'service-operations',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setVendorPayments((prev) => ([
        ...prev,
        {
          partyId: resolvedVendorId,
          partyName: vendorPaymentForm.vendorName || 'Unassigned Vendor',
          vendorId: resolvedVendorId,
          vendorName: vendorPaymentForm.vendorName || 'Unassigned Vendor',
          amount,
          category: 'Service Vendor Payment',
          source: 'service-operations',
        },
      ]));

      if (vendorPaymentForm.attachEmployeeWallet && vendorPaymentForm.employeeId) {
        const employee = employees.find(emp => emp.id === vendorPaymentForm.employeeId);
        if (!employee || (employee.status || 'active') !== 'active') {
          toast.error('Selected employee is inactive. Wallet entry can be recorded only for active employees.');
          return;
        }
        const modeText = String(vendorPaymentForm.paymentMode || '').toLowerCase();
        const mappedMode = modeText.includes('cash') ? 'cash' : 'upi';
        const companyData = { companyId: resolvedCompanyId, appId, userId };
        const employeeResult = await EmployeeCashManager.createCashTransaction(companyData, {
          employeeId: vendorPaymentForm.employeeId,
          employeeName: employee?.name || employee?.employeeName || 'Employee',
          amount,
          transactionType: 'outflow',
          paymentMode: mappedMode,
          category: 'Service Vendor Payment',
          description: `Wallet used for service vendor payment: ${vendorPaymentForm.vendorName || 'Unassigned Vendor'} (${receiptNo})`,
          referenceNumber: vendorPaymentForm.reference || receiptNo,
          linkedPaymentId: paymentDocRef.id,
          linkedPaymentType: 'service-vendor',
          linkedReceiptNumber: receiptNo,
          date: vendorPaymentForm.paymentDate,
          status: 'completed',
        });
        if (!employeeResult?.success) {
          toast.error(employeeResult?.error || 'Employee wallet entry failed.');
        }
      }

      toast.success('Vendor payment entry saved.');
      setShowVendorPaymentModal(false);
    } catch (error) {
      console.error('Failed to save vendor payment entry:', error);
      toast.error('Failed to save vendor payment.');
    } finally {
      setSavingVendorPayment(false);
    }
  };

  const pendingCount = useMemo(
    () => jobs.filter((j) => (j.status || 'pending') === 'pending').length,
    [jobs]
  );

  const statusPills = [
    { value: '', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'done', label: 'Done' },
    { value: 'paid', label: 'Paid' },
  ];

  const groupedByBill = useMemo(() => {
    const billMap = new Map();
    sortedFilteredJobs.forEach((job) => {
      const billKey = `${job.billId}__${job.billNo || ''}`;
      if (!billMap.has(billKey)) {
        billMap.set(billKey, {
          billKey,
          billId: job.billId,
          billNo: job.billNo,
          billDate: job.billDate,
          partyName: job.partyName,
          itemsMap: new Map(),
        });
      }
      const bill = billMap.get(billKey);
      const itemKey = `${job.rowIndex}__${job.itemId || ''}__${job.itemName || ''}`;
      if (!bill.itemsMap.has(itemKey)) {
        bill.itemsMap.set(itemKey, {
          itemKey,
          rowIndex: job.rowIndex,
          itemName: job.itemName || '—',
          measurementLabel: job.measurementLabel || '',
          services: [],
        });
      }
      bill.itemsMap.get(itemKey).services.push(job);
    });
    return Array.from(billMap.values()).map((bill) => {
      const items = Array.from(bill.itemsMap.values()).sort((a, b) => a.rowIndex - b.rowIndex);
      let serviceCount = 0;
      let pendingCount = 0;
      items.forEach((item) => {
        serviceCount += item.services.length;
        pendingCount += item.services.filter((s) => (s.status || 'pending') === 'pending').length;
      });
      return {
        billKey: bill.billKey,
        billId: bill.billId,
        billNo: bill.billNo,
        billDate: bill.billDate,
        partyName: bill.partyName,
        items,
        itemCount: items.length,
        serviceCount,
        pendingCount,
      };
    });
  }, [sortedFilteredJobs]);

  const billExpandDefaultsKey = useMemo(
    () =>
      [
        groupedByBill.map((b) => b.billKey).join('|'),
        filters.status,
        filters.tag,
        filters.vendor,
        filters.hasVendor,
        filters.invoiceVisibleOnly,
        sortBy,
        sortDirection,
      ].join('::'),
    [groupedByBill, filters.status, filters.tag, filters.vendor, filters.hasVendor, filters.invoiceVisibleOnly, sortBy, sortDirection]
  );

  useEffect(() => {
    if (viewMode !== 'operations') return;
    if (!groupedByBill.length) {
      setExpandedBillKeys(new Set());
      return;
    }
    const next = new Set();
    if (groupedByBill.length <= 3) {
      groupedByBill.forEach((b) => next.add(b.billKey));
    } else {
      const withPending = groupedByBill.filter((b) => b.pendingCount > 0);
      if (withPending.length > 0) {
        withPending.slice(0, 5).forEach((b) => next.add(b.billKey));
      } else {
        next.add(groupedByBill[0].billKey);
      }
    }
    setExpandedBillKeys(next);
  }, [billExpandDefaultsKey, groupedByBill, viewMode]);

  const toggleBillExpanded = useCallback((billKey) => {
    setExpandedBillKeys((prev) => {
      const next = new Set(prev);
      if (next.has(billKey)) next.delete(billKey);
      else next.add(billKey);
      return next;
    });
  }, []);

  const expandAllBills = useCallback(() => {
    setExpandedBillKeys(new Set(groupedByBill.map((b) => b.billKey)));
  }, [groupedByBill]);

  const collapseAllBills = useCallback(() => {
    setExpandedBillKeys(new Set());
  }, []);

  const renderServiceCard = (job, idx) => {
    const jobKey = getJobKey(job);
    const isSaving = savingKey === jobKey;
    const status = job.status || 'pending';
    const statusClass = SERVICE_STATUS_STYLES[status] || SERVICE_STATUS_STYLES.pending;
    return (
      <li
        key={`${job.billId}-${job.rowIndex}-${job.serviceId}-${idx}`}
        className={`rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${isSaving ? 'opacity-60' : ''}`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <span className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1 text-sm font-semibold text-indigo-900 capitalize">
            {job.serviceName || '—'}
          </span>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border capitalize ${statusClass}`}>
              {status}
            </span>
            {isSaving && <span className="text-xs text-slate-400">Saving…</span>}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block sm:col-span-2">
            <span className="text-xs font-medium text-slate-500 mb-1 block">Vendor</span>
            {(() => {
              const vendorOptions = getVendorOptionsForJob(job);
              const suggested = !job.vendorId ? getSuggestedVendorForJob(job) : null;
              return (
                <>
                  <AppSelect
                    value={job.vendorId || ''}
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      if (!vendorId) {
                        patchJob(jobKey, { vendorId: '', vendorName: '' });
                        return;
                      }
                      const vendor = parties.find((p) => p.id === vendorId);
                      patchJob(jobKey, {
                        vendorId,
                        vendorName: vendor?.firmName || vendor?.name || vendor?.partyName || '',
                      });
                      if (job?.serviceName) {
                        void rememberPreferredVendorForTag(job.serviceName, vendorId);
                      }
                    }}
                    placeholder="— Select vendor —"
                    aria-label="Vendor"
                    sheetTitle="Vendor"
                    options={[
                      { value: '', label: '— Select vendor —' },
                      ...vendorOptions.map((v) => ({
                        value: v.value,
                        label: `${v.isFavorite ? '★ ' : ''}${v.label}${v.favRank === 0 ? ' (primary)' : v.favRank > 0 ? ` (#${v.favRank + 1})` : ''}`,
                      })),
                    ]}
                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg px-2.5`}
                  />
                  {suggested && (
                    <button
                      type="button"
                      onClick={() => {
                        patchJob(jobKey, {
                          vendorId: suggested.id,
                          vendorName: suggested.firmName || suggested.name || '',
                        });
                        void rememberPreferredVendorForTag(job.serviceName, suggested.id);
                      }}
                      className="mt-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                    >
                      Apply favorite: {suggested.firmName}
                    </button>
                  )}
                </>
              );
            })()}
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-500 mb-1 block">Internal cost</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={job.internalCost ?? 0}
              onChange={(e) => handleInlineUpdate(jobKey, 'internalCost', e.target.value)}
              className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm"
            />
            {(() => {
              const tagMeta = findServiceTagMeta(serviceTags, job.serviceName);
              const rowLike = {
                qty: job.qty,
                nos: job.nos,
                length: job.length,
                height: job.height,
              };
              const suggested = tagMeta ? calculateInternalCostFromTag(tagMeta, rowLike) : null;
              if (suggested == null) return null;
              return (
                <span className="text-xs text-slate-400 mt-0.5 block">
                  Tag rate suggests {suggested}
                </span>
              );
            })()}
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-500 mb-1 block">Customer charge</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={job.customerCharge ?? 0}
              onChange={(e) => handleInlineUpdate(jobKey, 'customerCharge', e.target.value)}
              className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </label>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(job.showOnInvoice)}
              onChange={(e) => handleInlineUpdate(jobKey, 'showOnInvoice', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
            />
            Show on customer invoice
          </label>
          <AppSelect
            value={status}
            onChange={(e) => handleInlineUpdate(jobKey, 'status', e.target.value)}
            aria-label="Change status"
            sheetTitle="Status"
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'done', label: 'Done' },
              { value: 'paid', label: 'Paid' },
            ]}
            triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg px-2.5 py-1.5 min-h-[36px] w-auto`}
          />
        </div>
      </li>
    );
  };


  const renderTagCostComparison = (row) => {
    if (row.defaultRate == null) {
      return (
        <p className="text-[11px] text-slate-400 mt-1">
          Set default rate in <span className="font-medium">Service tags</span> to compare
        </p>
      );
    }
    const diff = row.costVsDefaultRate || 0;
    const diffLabel = diff === 0
      ? 'Matches default'
      : diff > 0
        ? `${formatCurrency(diff)} above default / job`
        : `${formatCurrency(Math.abs(diff))} below default / job`;
    return (
      <p className={`text-[11px] mt-1 font-medium ${diff > 0 ? 'text-amber-800' : diff < 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
        Default {formatCurrency(row.defaultRate)} / job · avg {formatCurrency(row.avgInternalPerJob)} · {diffLabel}
      </p>
    );
  };

  const renderVendorJobsTable = (jobDetails = []) => {
    if (!jobDetails.length) return null;
    return (
      <div className="max-h-[22rem] overflow-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 z-10 bg-gray-50">
            <tr>
              <th className="text-left font-medium text-gray-500 uppercase tracking-wider px-3 py-2.5 whitespace-nowrap">Invoice</th>
              <th className="text-left font-medium text-gray-500 uppercase tracking-wider px-3 py-2.5 min-w-[8rem]">Customer</th>
              <th className="text-left font-medium text-gray-500 uppercase tracking-wider px-3 py-2.5 min-w-[10rem]">Item &amp; service</th>
              <th className="text-left font-medium text-gray-500 uppercase tracking-wider px-3 py-2.5 whitespace-nowrap">Status</th>
              <th className="text-right font-medium text-gray-500 uppercase tracking-wider px-3 py-2.5 whitespace-nowrap">Amount</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {jobDetails.map((job, idx) => {
              const status = job.status || 'pending';
              const statusClass = SERVICE_STATUS_STYLES[status] || SERVICE_STATUS_STYLES.pending;
              return (
                <tr key={`${job.billId || idx}-${job.serviceName}-${idx}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <span className="font-semibold text-blue-700">{job.billNo || '—'}</span>
                    {job.billDate && (
                      <div className="text-gray-500 mt-0.5">{formatDateToCompanyFormat(job.billDate)}</div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 align-top text-gray-800 max-w-[12rem]">
                    <span className="line-clamp-2" title={job.partyName || ''}>{job.partyName || '—'}</span>
                  </td>
                  <td className="px-3 py-2.5 align-top min-w-[10rem]">
                    <div className="text-gray-900 font-medium line-clamp-2" title={job.itemName || ''}>{job.itemName || '—'}</div>
                    <span className="inline-block mt-0.5 capitalize text-[11px] text-gray-600">{job.serviceName || '—'}</span>
                    {job.measurementLabel ? (
                      <div className="text-gray-500 mt-0.5 truncate" title={job.measurementLabel}>{job.measurementLabel}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 align-top whitespace-nowrap">
                    <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border capitalize ${statusClass}`}>
                      {status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 align-top text-right whitespace-nowrap">
                    <div className="font-semibold text-gray-900">{formatCurrency(job.internalCost)}</div>
                    {job.customerCharge > 0 && (
                      <div className="text-emerald-600 text-[10px] mt-0.5">+{formatCurrency(job.customerCharge)} on bill</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderVendorReportCard = (row) => {
    const jobsExpanded = expandedVendorKeys.has(row.vendorKey);
    const jobCount = row.jobDetails?.length || row.totalJobs || 0;
    const hasPending = (row.pendingPayable || 0) > 0;
    const initial = (row.vendorName || 'V').charAt(0).toUpperCase();

    return (
      <article className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4">
          <div className="flex flex-wrap items-start gap-3">
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm ${
                row.vendorKey === 'unassigned'
                  ? 'bg-gradient-to-br from-gray-400 to-gray-500'
                  : 'bg-gradient-to-br from-blue-600 to-sky-500'
              }`}
            >
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-gray-900">{row.vendorName}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {row.totalJobs} job{row.totalJobs !== 1 ? 's' : ''}
                {row.pendingJobs > 0 ? ` · ${row.pendingJobs} pending` : ''}
              </p>
              {(row.tagsHandled || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {(row.tagsHandled || []).map((t) => (
                    <span key={t} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 capitalize">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <span
                className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                  hasPending ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                }`}
              >
                {hasPending ? 'Payable due' : 'Settled'}
              </span>
              {row.vendorKey !== 'unassigned' ? (
                <button
                  type="button"
                  onClick={() => openVendorPayment(row)}
                  className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 shadow-sm"
                >
                  Pay vendor
                </button>
              ) : (
                <span className="text-xs text-gray-500">Assign on Jobs tab</span>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div className="bg-gradient-to-br from-sky-50 to-blue-50 p-3 rounded-lg border border-blue-100 shadow-sm">
            <p className="text-xs text-blue-600 font-medium mb-1">Total internal</p>
            <p className="font-bold text-blue-800">{formatCurrency(row.totalInternalCost)}</p>
          </div>
          <div
            className={`p-3 rounded-lg border shadow-sm ${
              hasPending
                ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200'
                : 'bg-white border-gray-100'
            }`}
          >
            <p className={`text-xs font-medium mb-1 ${hasPending ? 'text-amber-700' : 'text-gray-500'}`}>Pending payable</p>
            <p className={`font-bold ${hasPending ? 'text-amber-800' : 'text-gray-400'}`}>{formatCurrency(row.pendingPayable)}</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg border border-green-200 shadow-sm">
            <p className="text-xs text-green-700 font-medium mb-1">Paid</p>
            <p className="font-bold text-green-600">{formatCurrency(row.paidAmount)}</p>
          </div>
        </div>

        {jobCount > 0 && (
          <div className="border-t border-gray-100 bg-slate-50/40 px-4 py-3">
            <button
              type="button"
              onClick={() => toggleVendorJobsExpanded(row.vendorKey)}
              aria-expanded={jobsExpanded}
              className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-gray-700 hover:text-blue-700 py-1"
            >
              <span>
                {jobsExpanded ? 'Hide job lines' : 'View job lines'}
                <span className="text-gray-500 font-normal ml-1">({jobCount})</span>
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200 ${jobsExpanded ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
            {jobsExpanded && <div className="mt-3">{renderVendorJobsTable(row.jobDetails)}</div>}
          </div>
        )}
      </article>
    );
  };

  const renderReportStatusPills = (pending, done, paid) => (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {pending > 0 && (
        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
          {pending} pending
        </span>
      )}
      {done > 0 && (
        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-900 border border-emerald-200">
          {done} done
        </span>
      )}
      {paid > 0 && (
        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-900 border border-violet-200">
          {paid} paid
        </span>
      )}
    </div>
  );

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Service Operations</h2>
          <p className="mt-1 text-sm text-gray-600">
            {viewMode === 'reports' ? (
              <>Costs, payables, and tag breakdown for filtered service jobs.</>
            ) : (
              <>
                Bills → line items → service tags. Edit any field; changes save automatically.
                {pendingCount > 0 && <span className="ml-1 font-medium text-amber-700">({pendingCount} pending)</span>}
              </>
            )}
            {' '}
            <Link to="/service-tags" className="text-blue-600 hover:underline">
              Manage tag rates &amp; vendors
            </Link>
          </p>
        </div>
        <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 shrink-0">
          <button type="button" onClick={() => setViewMode('operations')} className={`px-4 py-2 rounded-md text-sm font-medium ${viewMode === 'operations' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>Jobs</button>
          <button type="button" onClick={() => setViewMode('reports')} className={`px-4 py-2 rounded-md text-sm font-medium ${viewMode === 'reports' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}>Reports</button>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-3 md:p-4 shadow-sm">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Filters</p>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {statusPills.map((pill) => (
            <button key={pill.value || 'all'} type="button" onClick={() => setFilters((prev) => ({ ...prev, status: pill.value }))} className={`px-3 py-1.5 rounded-full text-sm font-medium ${filters.status === pill.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{pill.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <label className="flex flex-col gap-1 min-w-[8rem] flex-1 max-w-xs"><span className="text-xs text-gray-500">Tag</span>
            <AppSelect
              value={filters.tag}
              onChange={(e) => setFilters((prev) => ({ ...prev, tag: e.target.value }))}
              aria-label="Tag filter"
              sheetTitle="Tag"
              options={[
                { value: '', label: 'All tags' },
                ...tagFilterGroups.flatMap((group) =>
                  group.tags.map((tag) => ({ value: tag, label: tag }))
                ),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg`}
            />
          </label>
          <label className="flex flex-col gap-1 min-w-[8rem] flex-1 max-w-xs"><span className="text-xs text-gray-500">Vendor</span>
            <AppSelect
              value={filters.vendor}
              onChange={(e) => setFilters((prev) => ({ ...prev, vendor: e.target.value }))}
              aria-label="Vendor filter"
              sheetTitle="Vendor"
              options={[
                { value: '', label: 'All' },
                ...vendorOptions.map((v) => ({ value: v.value, label: v.label })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg`}
            />
          </label>
          {viewMode === 'operations' && (
            <>
              <label className="flex flex-col gap-1"><span className="text-xs text-gray-500">Sort</span>
                <AppSelect
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  aria-label="Sort by"
                  sheetTitle="Sort by"
                  options={[
                    { value: 'billDate', label: 'Date' },
                    { value: 'billNo', label: 'Bill #' },
                    { value: 'vendorName', label: 'Vendor' },
                    { value: 'status', label: 'Status' },
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg`}
                />
              </label>
              <button type="button" onClick={() => setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))} className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100">{sortDirection === 'desc' ? '↓ Newest' : '↑ Oldest'}</button>
            </>
          )}
          <button type="button" onClick={() => setShowAdvancedFilters((v) => !v)} className="px-3 py-2 text-sm text-blue-700 hover:underline">{showAdvancedFilters ? 'Less' : 'More'}</button>
        </div>
        {showAdvancedFilters && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-4 items-center">
            <label className="flex flex-col gap-1"><span className="text-xs text-gray-500">Vendor assigned</span>
              <AppSelect
                value={filters.hasVendor}
                onChange={(e) => setFilters((prev) => ({ ...prev, hasVendor: e.target.value }))}
                aria-label="Vendor assigned"
                sheetTitle="Vendor assigned"
                options={[
                  { value: 'all', label: 'Any' },
                  { value: 'yes', label: 'Has vendor' },
                  { value: 'no', label: 'No vendor' },
                ]}
                triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg`}
              />
            </label>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={filters.invoiceVisibleOnly} onChange={(e) => setFilters((prev) => ({ ...prev, invoiceVisibleOnly: e.target.checked }))} className="h-4 w-4" />Invoice-visible only</label>
          </div>
        )}
      </div>

      {viewMode === 'reports' && (
      <div className="mb-6 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">Service jobs</p>
            <p className="mt-1 text-2xl font-bold text-gray-900">{topSummary.totalServiceJobs || 0}</p>
            <p className="text-xs text-gray-500 mt-1">{topSummary.pendingJobs || 0} pending</p>
          </div>
          <div className="rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50 to-white p-4 shadow-sm">
            <p className="text-[10px] text-amber-700 uppercase tracking-wide font-medium">Pending</p>
            <p className="mt-1 text-2xl font-bold text-amber-800">{topSummary.pendingJobs || 0}</p>
            <p className="text-xs text-gray-500 mt-1">{topSummary.doneJobs || 0} done · {topSummary.paidJobs || 0} paid</p>
          </div>
          <div className="rounded-xl border border-red-100 bg-gradient-to-br from-red-50 to-rose-50 p-4 shadow-sm">
            <p className="text-[10px] text-red-700 uppercase tracking-wide font-medium">Vendor payable</p>
            <p className="mt-1 text-xl font-bold text-red-600">{formatCurrency(topSummary.totalVendorPayable || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">Internal {formatCurrency(topSummary.totalInternalCost || 0)}</p>
          </div>
          <div className="rounded-xl border border-green-100 bg-gradient-to-br from-green-50 to-emerald-50 p-4 shadow-sm">
            <p className="text-[10px] text-green-700 uppercase tracking-wide font-medium">Customer charges</p>
            <p className="mt-1 text-xl font-bold text-green-600">{formatCurrency(topSummary.totalCustomerServiceCharge || 0)}</p>
            <p className="text-xs text-gray-500 mt-1">On invoice {formatCurrency(topSummary.invoiceVisibleServiceRevenue || 0)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm">
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            <button
              type="button"
              onClick={() => setReportTab('vendors')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${reportTab === 'vendors' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}
            >
              By vendor
            </button>
            <button
              type="button"
              onClick={() => setReportTab('tags')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${reportTab === 'tags' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600'}`}
            >
              By tag
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={exportAllServiceReports}
              className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50"
            >
              Export all CSV
            </button>
            <GlobalExportButtons
              data={reportTabExportPayload.data}
              columns={reportTabExportPayload.columns}
              filename={reportTabExportPayload.filename}
              title={reportTabExportPayload.title}
              companyDetails={companyDetailsForExport}
              reportDetails={{ TotalJobsAfterFilters: filteredJobs.length }}
            />
          </div>
        </div>

        {reportTab === 'tags' && rateBasisFilterOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/30 px-3 py-2">
            <span className="text-xs font-medium text-indigo-900 w-full sm:w-auto">Rate type:</span>
            <button
              type="button"
              onClick={() => setFilters((prev) => ({ ...prev, rateBasis: '' }))}
              className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                !filters.rateBasis ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All types
            </button>
            {rateBasisFilterOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, rateBasis: opt.value }))}
                className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                  filters.rateBasis === opt.value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {opt.label}
                <span className="opacity-80 ml-1">({opt.count})</span>
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {loading ? (
        <div className="text-gray-600 py-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600 mb-3" aria-hidden />
          <p className="text-sm font-medium text-gray-800">Loading service operations…</p>
          <p className="mt-1 text-xs text-gray-500">Fetching bills and parties</p>
        </div>
      ) : (
        viewMode === 'operations' ? (
        <div className="space-y-4">
          {groupedByBill.length > 1 && (
            <div className="flex flex-wrap items-center justify-end gap-2 text-sm">
              <span className="text-xs text-slate-500 mr-auto">
                {groupedByBill.length} invoice{groupedByBill.length !== 1 ? 's' : ''} · tap header to expand
              </span>
              <button
                type="button"
                onClick={expandAllBills}
                className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              >
                Expand all
              </button>
              <button
                type="button"
                onClick={collapseAllBills}
                className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              >
                Collapse all
              </button>
            </div>
          )}
          {groupedByBill.map((bill) => {
            const isExpanded = expandedBillKeys.has(bill.billKey);
            return (
            <div key={bill.billKey} className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden">
              <button
                type="button"
                onClick={() => toggleBillExpanded(bill.billKey)}
                aria-expanded={isExpanded}
                className="w-full px-4 py-3.5 text-left bg-gradient-to-r from-slate-100 to-slate-50 border-b border-slate-200 border-l-4 border-l-blue-600 hover:from-slate-200/80 hover:to-slate-100/80 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                      {bill.billDate ? formatDateToCompanyFormat(bill.billDate) : 'No date'}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
                      <span className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-1 text-base sm:text-lg font-bold text-white shadow-sm tracking-tight">
                        {bill.billNo || '—'}
                      </span>
                      <span className="text-slate-400 font-light hidden sm:inline select-none" aria-hidden>·</span>
                      <span className="text-base sm:text-lg font-semibold text-slate-900 min-w-0 truncate" title={bill.partyName || ''}>
                        {bill.partyName || '—'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-2">
                      {bill.itemCount} item{bill.itemCount !== 1 ? 's' : ''}
                      {' · '}
                      {bill.serviceCount} service{bill.serviceCount !== 1 ? 's' : ''}
                      {bill.pendingCount > 0 && (
                        <span className="text-amber-800 font-semibold">
                          {' · '}
                          {bill.pendingCount} pending
                        </span>
                      )}
                    </p>
                  </div>
                  <ChevronDown
                    className={`h-5 w-5 shrink-0 text-slate-500 mt-1 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </div>
              </button>
              {isExpanded && (
              <div className="divide-y divide-slate-100">
              {bill.items.map((item, itemIdx) => (
                <div key={item.itemKey} className="px-4 py-4 bg-slate-50/40">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white">
                      {itemIdx + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-semibold text-slate-900">{item.itemName}</h4>
                      {item.measurementLabel ? (
                        <p className="text-xs text-indigo-700 font-medium mt-0.5 truncate" title={item.measurementLabel}>
                          {item.measurementLabel}
                        </p>
                      ) : null}
                    </div>
                    <span className="text-xs font-medium text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200 shrink-0">
                      {item.services.length} service{item.services.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <ul className="space-y-2 list-none m-0 p-0">
                    {item.services.map((job, idx) => renderServiceCard(job, idx))}
                  </ul>
                </div>
              ))}
              </div>
              )}
            </div>
            );
          })}
          {groupedByBill.length === 0 && (
            <div className="border border-dashed border-gray-300 rounded-xl px-6 py-12 text-center bg-gray-50/50">
              <p className="font-medium text-gray-700">No jobs match your filters</p>
              <p className="mt-1 text-sm text-gray-500">Add tags on invoice lines in Sales, then return here.</p>
            </div>
          )}
        </div>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-sky-50 via-white to-amber-50 flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {reportTab === 'vendors' ? 'Vendor payables' : 'Tag performance'}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {filteredJobs.length} job{filteredJobs.length !== 1 ? 's' : ''} after filters
                  {reportTab === 'vendors' ? ' · expand a vendor to see invoice lines' : ''}
                </p>
              </div>
              {reportTab === 'vendors' && vendorWiseRows.length > 1 && (
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    onClick={expandAllVendorJobs}
                    className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50"
                  >
                    Expand all jobs
                  </button>
                  <button
                    type="button"
                    onClick={collapseAllVendorJobs}
                    className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50"
                  >
                    Collapse all
                  </button>
                </div>
              )}
            </div>
            <div className="p-3 sm:p-4 space-y-3">
            {reportTab === 'tags' ? (
              tagWiseRowsForReports.length === 0 ? (
                <div className="border border-dashed border-gray-300 rounded-xl px-6 py-12 text-center bg-gray-50/50">
                  <p className="font-medium text-gray-700">No tag data for current filters</p>
                  <p className="mt-1 text-sm text-gray-500">Add service tags on invoice lines in Sales, or configure tags under Service tags.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {tagWiseGroupedByBasis.map((group) => (
                    <section
                      key={group.rateBasis}
                      className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm"
                    >
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{group.rateBasisLabel}</h3>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {group.rows.length} tag{group.rows.length !== 1 ? 's' : ''} · {group.usageCount} job{group.usageCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex gap-2 text-right text-sm">
                          <div className="bg-gradient-to-br from-sky-50 to-blue-50 px-3 py-1.5 rounded-lg border border-blue-100">
                            <p className="text-[10px] uppercase text-blue-600 font-medium">Internal</p>
                            <p className="font-bold text-blue-800">{formatCurrency(group.totalInternalCost)}</p>
                          </div>
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 px-3 py-1.5 rounded-lg border border-green-200">
                            <p className="text-[10px] uppercase text-green-700 font-medium">Customer</p>
                            <p className="font-bold text-green-600">{formatCurrency(group.totalCustomerCharge)}</p>
                          </div>
                        </div>
                      </div>
                      <ul className="space-y-2 list-none m-0 p-3">
                        {group.rows.map((row) => (
                          <li
                            key={row.tag}
                            className="rounded-lg border border-slate-200 bg-white overflow-hidden"
                          >
                            <div className="flex flex-wrap items-stretch">
                              <div className="flex-1 min-w-0 px-4 py-3 border-r border-slate-100">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-semibold text-slate-900 capitalize">{row.tag}</p>
                                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                                    {row.rateBasisShortLabel}
                                  </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">{row.usageCount} job{row.usageCount !== 1 ? 's' : ''}</p>
                                {renderReportStatusPills(row.pendingCount, row.doneCount, row.paidCount)}
                                {renderTagCostComparison(row)}
                              </div>
                              <div className="flex shrink-0 divide-x divide-gray-100">
                                <div className="px-4 py-3 text-center min-w-[5.5rem] bg-gradient-to-br from-sky-50 to-blue-50">
                                  <p className="text-[10px] uppercase text-blue-600 font-medium">Internal</p>
                                  <p className="text-sm font-bold text-blue-800 mt-0.5">{formatCurrency(row.totalInternalCost)}</p>
                                </div>
                                <div className="px-4 py-3 text-center min-w-[5.5rem] bg-gradient-to-br from-green-50 to-emerald-50">
                                  <p className="text-[10px] uppercase text-green-700 font-medium">Customer</p>
                                  <p className="text-sm font-bold text-green-600 mt-0.5">{formatCurrency(row.totalCustomerCharge)}</p>
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )
            ) : vendorWiseRows.length === 0 ? (
              <div className="border border-dashed border-gray-300 rounded-xl px-6 py-12 text-center bg-gray-50/50">
                <p className="font-medium text-gray-700">No vendor data for current filters</p>
                <p className="mt-1 text-sm text-gray-500">Assign vendors on jobs in the Jobs tab.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {vendorWiseRows.map((row) => (
                  <div key={row.vendorKey}>{renderVendorReportCard(row)}</div>
                ))}
              </div>
            )}
            </div>
          </div>
        )
      )}

      {showVendorPaymentModal && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[100] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full max-w-2xl max-h-[92dvh] overflow-y-auto border border-gray-200">
            <div className="px-4 sm:px-6 py-4 border-b bg-gray-50 rounded-t-2xl sm:rounded-t-xl shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Add Vendor Payment</h3>
              <p className="text-xs text-gray-500 mt-1">Compatible with Payments ledger and optional employee wallet flow.</p>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Vendor</label>
                <input value={vendorPaymentForm.vendorName} readOnly className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Date</label>
                <input type="date" value={vendorPaymentForm.paymentDate} onChange={(e) => setVendorPaymentForm(p => ({ ...p, paymentDate: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                <input type="number" min="0" step="0.01" value={vendorPaymentForm.amount} onChange={(e) => setVendorPaymentForm(p => ({ ...p, amount: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="0.00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment Mode</label>
                <AppSelect
                  value={vendorPaymentForm.paymentMode}
                  onChange={(e) => setVendorPaymentForm(p => ({ ...p, paymentMode: e.target.value }))}
                  aria-label="Payment mode"
                  sheetTitle="Payment mode"
                  options={[
                    { value: 'Cash', label: 'Cash' },
                    { value: 'UPI', label: 'UPI' },
                    { value: 'Bank Transfer', label: 'Bank Transfer' },
                    { value: 'Cheque', label: 'Cheque' },
                  ]}
                  triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-lg`}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reference</label>
                <input value={vendorPaymentForm.reference} onChange={(e) => setVendorPaymentForm(p => ({ ...p, reference: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Txn/Ref no." />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input value={vendorPaymentForm.notes} onChange={(e) => setVendorPaymentForm(p => ({ ...p, notes: e.target.value }))} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Reason/description" />
              </div>
              <div className="md:col-span-2 border border-blue-100 rounded-lg p-3 bg-blue-50/50">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-blue-900">
                  <input type="checkbox" checked={vendorPaymentForm.attachEmployeeWallet} onChange={(e) => setVendorPaymentForm(p => ({ ...p, attachEmployeeWallet: e.target.checked }))} />
                  Also add employee wallet entry
                </label>
                {vendorPaymentForm.attachEmployeeWallet && (
                  <AppSelect
                    value={vendorPaymentForm.employeeId}
                    onChange={(e) => setVendorPaymentForm(p => ({ ...p, employeeId: e.target.value }))}
                    placeholder="Select Employee"
                    aria-label="Select employee"
                    sheetTitle="Select employee"
                    options={[
                      { value: '', label: 'Select Employee' },
                      ...employees.filter(emp => (emp.status || 'active') === 'active').map(emp => ({
                        value: emp.id,
                        label: emp.name || emp.employeeName || emp.employeeId || emp.id,
                      })),
                    ]}
                    triggerClassName={`${APP_SELECT_FILTER_TRIGGER} mt-2 rounded-lg`}
                  />
                )}
              </div>
            </div>
            <div className="px-4 sm:px-6 py-4 border-t bg-gray-50 rounded-b-2xl sm:rounded-b-xl flex flex-col-reverse sm:flex-row sm:justify-end gap-2 shrink-0">
              <button type="button" onClick={() => setShowVendorPaymentModal(false)} className="min-h-[44px] px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 touch-manipulation">Cancel</button>
              <button type="button" disabled={savingVendorPayment} onClick={saveVendorPayment} className="min-h-[44px] px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60 touch-manipulation">
                {savingVendorPayment ? 'Saving...' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ServiceOperations;
